import React, { useCallback, useEffect, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import { exportReviewsCsv } from './adminExportUtils';
import AdminToggle from './AdminToggle';
import {
  AdminPanelAlert,
  AdminPanelEmpty,
  AdminPanelHero,
  AdminPanelListCard,
  AdminPanelRow,
  AdminPanelToolbar,
} from './AdminPanelUi';

function Stars({ rating }) {
  const n = Math.max(1, Math.min(5, Number(rating) || 5));
  return <span aria-label={`${n} stars`}>{'★'.repeat(n)}{'☆'.repeat(5 - n)}</span>;
}

export default function AdminReviewsPanel({ sessionToken, setStatus }) {
  const [reviews, setReviews] = useState([]);
  const [pendingOnly, setPendingOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = pendingOnly ? '?pending=1' : '';
      const r = await fetch(`/api/admin_reviews.php${qs}`, { headers: adminAuthHeaders(sessionToken) });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Could not load reviews.');
        setReviews([]);
        return;
      }
      setReviews(Array.isArray(d.reviews) ? d.reviews : []);
    } catch {
      setError('Could not load reviews.');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, pendingOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setActingId(id);
    setError('');
    try {
      const r = await fetch('/api/admin_reviews.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({ action, id }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Action failed.');
        return;
      }
      setStatus(action === 'approve' ? 'Review approved.' : 'Review removed.');
      await load();
    } catch {
      setError('Action failed.');
    } finally {
      setActingId(0);
    }
  };

  return (
    <div className="admin-reviews admin-panel-workspace-root">
      <AdminPanelHero
        title="Product reviews"
        lead="Approve customer reviews before they appear on product pages."
        actions={
          <AdminPanelToolbar>
            <AdminToggle
              id="reviews-pending"
              label="Pending only"
              checked={pendingOnly}
              onChange={setPendingOnly}
            />
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={!reviews.length}
              onClick={() => exportReviewsCsv(reviews)}
            >
              Export CSV
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={load}>
              Refresh
            </button>
          </AdminPanelToolbar>
        }
      />

      {error ? <AdminPanelAlert variant="danger">{error}</AdminPanelAlert> : null}

      <AdminPanelListCard
        kicker="Reviews"
        title={pendingOnly ? 'Awaiting approval' : 'All reviews'}
        count={reviews.length}
      >
        {loading ? (
          <AdminPanelEmpty message="Loading reviews…" />
        ) : reviews.length === 0 ? (
          <AdminPanelEmpty
            message={pendingOnly ? 'No reviews waiting for approval.' : 'No reviews yet.'}
          />
        ) : (
          reviews.map((rev) => (
            <AdminPanelRow key={rev.id}>
              <div className="admin-panel-row__top">
                <strong>{rev.author_name || 'Customer'}</strong>
                <span className="small text-muted">
                  {rev.created_at ? new Date(rev.created_at).toLocaleString('en-IN') : ''}
                </span>
              </div>
              <p className="admin-panel-row__meta mb-1">
                {rev.product_name || rev.product_external_id}
                {rev.category_slug ? ` · ${rev.category_slug}` : ''}
              </p>
              <p className="mb-2">
                <Stars rating={rev.rating} />
                {!rev.is_approved ? (
                  <span className="badge bg-warning text-dark ms-2">Pending</span>
                ) : null}
              </p>
              <p className="admin-panel-row__body">{rev.body}</p>
              <div className="admin-panel-row__actions">
                {!rev.is_approved ? (
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    disabled={actingId === rev.id}
                    onClick={() => act(rev.id, 'approve')}
                  >
                    Approve
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={actingId === rev.id}
                    onClick={() => act(rev.id, 'unapprove')}
                  >
                    Hide
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  disabled={actingId === rev.id}
                  onClick={() => act(rev.id, 'delete')}
                >
                  Delete
                </button>
              </div>
            </AdminPanelRow>
          ))
        )}
      </AdminPanelListCard>
    </div>
  );
}
